import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spin, message } from 'antd';
import { 
  SafetyOutlined, 
  EyeOutlined, 
  CalendarOutlined 
} from '@ant-design/icons';
import { usersAPI } from '@/services/api/users';
import { RoleInfo } from '@/types/api';

const RolePermissionCards: React.FC = () => {
  const [rolePermissions, setRolePermissions] = useState<{ [key: string]: RoleInfo }>({});
  const [loading, setLoading] = useState(true);

  // 获取角色权限数据
  const fetchRolePermissions = async () => {
    try {
      setLoading(true);
      const permissions = await usersAPI.getRolePermissions();
      setRolePermissions(permissions);
    } catch (error) {
      console.error('获取角色权限失败:', error);
      message.error('获取角色权限失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchRolePermissions();
  }, []);

  // 获取角色图标
  const getRoleIcon = (role: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'admin': <SafetyOutlined style={{ fontSize: 24, color: '#ef4444' }} />,
      'publisher': <EyeOutlined style={{ fontSize: 24, color: '#3b82f6' }} />,
      'viewer': <CalendarOutlined style={{ fontSize: 24, color: '#06b6d4' }} />,
    };
    return iconMap[role] || <SafetyOutlined style={{ fontSize: 24, color: '#6b7280' }} />;
  };

  // 获取角色颜色
  const getRoleColor = (role: string) => {
    const colorMap: { [key: string]: string } = {
      'admin': '#ef4444',
      'publisher': '#3b82f6',
      'viewer': '#06b6d4',
    };
    return colorMap[role] || '#6b7280';
  };

  // 获取权限名称
  const getPermissionName = (permission: string) => {
    const nameMap: { [key: string]: string } = {
      'version:create': '版本创建',
      'version:manage': '版本管理',
      'version:view': '版本查看',
      'strategy:config': '策略配置',
      'strategy:publish': '策略发布',
      'user:manage': '用户管理',
      'system:config': '系统配置',
      'audit:view': '审计日志',
      'progress:monitor': '进度监控',
      'device:view': '设备查看',
      'package:verify': '包验证',
      'dashboard:view': '仪表板',
      'api:view': 'API访问',
    };
    return nameMap[permission] || permission;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">角色权限说明</h3>
          <p className="text-gray-400 text-sm">各角色的功能权限范围</p>
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#9ca3af' }}>加载角色权限信息中...</p>
        </div>
      </div>
    );
  }

  const roleList = Object.values(rolePermissions);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">角色权限说明</h3>
        <p className="text-gray-400 text-sm">各角色的功能权限范围</p>
      </div>
      
      <Row gutter={[16, 16]}>
        {roleList.map((role) => {
          const roleColor = getRoleColor(role.role);
          const roleIcon = getRoleIcon(role.role);
          
          return (
            <Col xs={24} sm={12} lg={8} key={role.role}>
              <Card
                className="h-full"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px',
                }}
                styles={{
                  body: {
                    padding: '20px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }
                }}
              >
                <div className="flex items-center mb-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-3"
                    style={{ backgroundColor: `${roleColor}20` }}
                  >
                    {roleIcon}
                  </div>
                  <div>
                    <h4 
                      className="text-lg font-semibold mb-0"
                      style={{ color: roleColor }}
                    >
                      {role.name}
                    </h4>
                    <p className="text-sm text-gray-400 mb-0">{role.description}</p>
                  </div>
                </div>
                
                <div className="flex-1">
                  <ul className="space-y-2">
                    {role.permissions.map((permission, index) => (
                      <li 
                        key={index}
                        className="flex items-start text-sm text-gray-300"
                      >
                        <span 
                          className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0"
                          style={{ backgroundColor: roleColor }}
                        />
                        {getPermissionName(permission)}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default RolePermissionCards;
